import axios from 'axios';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { ppaUrls } from 'config/urls';
import { usePpaQueryFilters } from 'hooks/ppa/usePpaQueryFilters';

type PerformanceSummaryDrilldownFilter = {
    dimension: string;
    attribute_type: string;
    dim_attribute_ex_id: string;
    dim_attribute_ex_id_desc: string;
    is_attribute: boolean;
};

export type PerformanceSummaryOrderByItem = {
    id_type: 'attribute_type' | 'kpi_id';
    id: string;
    direction: 'asc' | 'desc';
};

type UsePerformanceSummaryKpisParams = {
    queryKey: QueryKey;
    kpiIds: string[];
    dimension?: string;
    aggregateAttributeTypes: string[];
    aggregateDimensions?: Array<string | undefined>;
    aggregateIsAttributes?: Array<boolean | undefined>;
    drilldownFilters?: PerformanceSummaryDrilldownFilter[];
    orderBy?: PerformanceSummaryOrderByItem[] | null;
    enabled: boolean;
    normalizeData?: (rows: unknown[]) => unknown[];
    pageSize?: number;
    userInputs?: PerformanceSummaryUserInput[];
    featureMeta?: PerformanceSummaryFeatureMeta;
};

export const PERFORMANCE_SUMMARY_PAGE_SIZE = 100;

/** Fixed viewport height for InfiniteScroller wrapping Performance Summary tables. */
export const PERFORMANCE_SUMMARY_INFINITE_SCROLL_HEIGHT = 555;

type KpisResponse<TNode> = { data: TNode[] };

/** Backend may include total row count under different keys; extend as API contract solidifies. */
function parseTotalFromPayload(payload: unknown): number | undefined {
    if (!payload || typeof payload !== 'object') return undefined;
    const p = payload as Record<string, unknown>;
    const meta = p.meta as Record<string, unknown> | undefined;
    const candidates = [p.total, p.count, p.total_count, meta?.total, meta?.total_count];
    for (const c of candidates) {
        if (typeof c === 'number' && Number.isFinite(c)) return c;
        if (typeof c === 'string' && c.trim() !== '') {
            const n = Number(c);
            if (Number.isFinite(n)) return n;
        }
    }
    return undefined;
}

type PerformanceSummaryKpisPage<TNode> = {
    nodes: TNode[];
    total?: number;
    offset: number;
    limit: number;
};

type PerformanceSummaryUserInput = {
    input_id: string;
    input_val: number | string;
};

type PerformanceSummaryFeatureMeta = {
    feature_id: string;
    tab?: {
        id_type: string;
        id: string;
    };
};

type BuildKpiRequestBodyArgs = {
    kpiIds: string[];
    dimension?: string;
    aggregateAttributeTypes: string[];
    aggregateDimensions?: Array<string | undefined>;
    aggregateIsAttributes?: Array<boolean | undefined>;
    drilldownFilters?: PerformanceSummaryDrilldownFilter[];
    orderBy?: PerformanceSummaryOrderByItem[] | null;
    limit: number;
    offset: number;
    timePeriod: { start_timestamp?: number; end_timestamp?: number };
    filters: { dim1_id_list: unknown; dim2_id_list: unknown };
    userInputs?: PerformanceSummaryUserInput[];
    featureMeta?: PerformanceSummaryFeatureMeta;
};

const buildKpiRequestBody = ({
    kpiIds,
    dimension,
    aggregateAttributeTypes,
    aggregateDimensions,
    aggregateIsAttributes,
    drilldownFilters,
    orderBy,
    limit,
    offset,
    timePeriod,
    filters,
    userInputs,
    featureMeta,
}: BuildKpiRequestBodyArgs) => ({
    kpi_ids: kpiIds,
    time_period: timePeriod,
    filters,
    aggregates: aggregateAttributeTypes.map((attributeType, index) => {
        const aggregateDimension = aggregateDimensions?.[index] ?? dimension;
        const aggregateIsAttribute = aggregateIsAttributes?.[index] ?? true;
        return {
            dimension: aggregateDimension,
            attribute_type: attributeType,
            is_attribute: aggregateIsAttribute,
        };
    }),
    ...(drilldownFilters ? { drilldown_filters: drilldownFilters } : {}),
    ...(orderBy?.length ? { order_by: orderBy } : {}),
    include_growth: true,
    limit,
    offset,
    ...(userInputs?.length ? { user_inputs: userInputs } : {}),
    ...(featureMeta ? { feature_meta: featureMeta } : {}),
});

/**
 * Paginated KPI fetch for Performance Summary tables (infinite scroll).
 * Each page returns a slice of tree nodes; flatten with toRows / toLensRows in the section.
 */
const usePerformanceSummaryKpis = <TNode>({
    queryKey,
    kpiIds,
    dimension,
    aggregateAttributeTypes,
    aggregateDimensions,
    aggregateIsAttributes,
    drilldownFilters,
    orderBy,
    enabled,
    normalizeData,
    pageSize = PERFORMANCE_SUMMARY_PAGE_SIZE,
    userInputs,
    featureMeta,
}: UsePerformanceSummaryKpisParams) => {
    const {
        dim1_id_list,
        dim2_id_list,
        hasResolvedFilterIds,
        startTimestamp,
        endTimestamp,
        hasValidTimePeriod,
        filtersLoading,
        useGlobalFilterForQueries,
    } = usePpaQueryFilters();

    const dim1QueryKey = JSON.stringify(dim1_id_list ?? []);
    const dim2QueryKey = JSON.stringify(dim2_id_list ?? []);
    const orderByQueryKey = JSON.stringify(orderBy ?? []);
    const userInputsQueryKey = JSON.stringify(userInputs ?? []);
    const featureMetaQueryKey = JSON.stringify(featureMeta ?? {});
    const globalFilterQueryKey = useGlobalFilterForQueries ? 'applied' : 'scope-only';

    const timePeriod = {
        start_timestamp: startTimestamp,
        end_timestamp: endTimestamp,
    };
    const filters = {
        dim1_id_list,
        dim2_id_list,
    };
    const canFetchKpis =
        enabled &&
        !filtersLoading &&
        hasResolvedFilterIds &&
        hasValidTimePeriod;

    return useInfiniteQuery({
        queryKey: [
            queryKey,
            startTimestamp,
            endTimestamp,
            dim1QueryKey,
            dim2QueryKey,
            globalFilterQueryKey,
            orderByQueryKey,
            userInputsQueryKey,
            featureMetaQueryKey,
            pageSize,
        ],
        queryFn: async ({ pageParam }): Promise<PerformanceSummaryKpisPage<TNode>> => {
            const offset = pageParam as number;
            const response = await axios.post(
                ppaUrls.getPerformanceSummaryKpisUrl,
                buildKpiRequestBody({
                    kpiIds,
                    dimension,
                    aggregateAttributeTypes,
                    aggregateDimensions,
                    aggregateIsAttributes,
                    drilldownFilters,
                    orderBy,
                    limit: pageSize,
                    offset,
                    timePeriod,
                    filters,
                    userInputs,
                    featureMeta,
                }),
            );

            const payload = response.data as KpisResponse<unknown> & Record<string, unknown>;
            const rows = Array.isArray(payload?.data) ? payload.data : [];
            const normalizedRows = normalizeData ? normalizeData(rows) : rows;
            const total = parseTotalFromPayload(payload);

            return {
                nodes: normalizedRows as TNode[],
                total,
                offset,
                limit: pageSize,
            };
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            const nextOffset = lastPage.offset + lastPage.limit;
            const total = allPages[0]?.total;
            if (total !== undefined) return nextOffset < total ? nextOffset : undefined;
            if (lastPage.nodes.length < pageSize) return undefined;
            return nextOffset;
        },
        // Wait until inherited scope (or applied global filters) and time period are ready.
        enabled: canFetchKpis,
        // Keep previous rows during refetch to avoid table flicker.
        placeholderData: (previousData) => previousData,
        staleTime: 5 * 60 * 1000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
};

export type { PerformanceSummaryDrilldownFilter };
export default usePerformanceSummaryKpis;
